import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../constants/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackInitiateCheckout } from '../../lib/metaPixel';

/* Plan cards + live pricing + the real checkout flow, extracted out of
   PlansCompareModal so the same code renders both inside that modal
   (onboarding) and inside PlansPage.js (the full-page /planes destination
   every in-app "upgrade" CTA now points to, via UpgradeModal). Keeping
   this in one place means POST /subscriptions/checkout only exists once. */
const PLANS = [
  {
    id: 'free', name: 'Gratuito', desc: 'Gratis para siempre. Comparte tu contacto como un profesional, sin límite de tiempo.',
    features: ['Conexiones ilimitadas', '1 perfil digital', 'Analíticas básicas', 'Leads limitados /mes', 'Apple Wallet & Google Wallet', 'QR code y link único'],
    price: '$0', priceYearly: '$0', priceSub: 'para siempre', priceYearlySub: 'para siempre',
    cta: 'Continuar', featured: false,
  },
  {
    id: 'pro', name: 'Pro', desc: 'Para profesionales que quieren saber qué pasa con cada contacto y actuar antes de que se enfríe.',
    features: ['Todo lo del Gratuito', 'Analytics avanzadas', 'Leads ilimitados', 'Identificación de leads', 'Seguimiento inteligente', 'Personalización avanzada', 'Integraciones con tus herramientas', 'Acceso API y Webhooks'],
    price: '$2.500', priceYearly: '$25.000', priceSub: '/mes · por perfil', priceYearlySub: '/año · por perfil · cobrado anual',
    cta: 'Empezar con Pro', featured: true,
  },
  {
    id: 'enterprise', name: 'Enterprise', desc: 'Para negocios con varias personas en terreno: un panel central para ver quién está capturando y convirtiendo en cada evento.',
    features: ['Ranking de equipo por campaña', 'Múltiples perfiles administrados', 'Analítica de equipo', 'Gestión de equipos (Teams)', 'Soporte prioritario', 'Todo lo del Pro'],
    contactSales: true, priceTagline: 'Precio a medida según el tamaño de tu equipo.',
    cta: 'Agendar reunión', featured: false,
  },
];

const planT = (t, id, field, fallback) => t(`plansCompare.${id}.${field}`, fallback);
const planFeatureT = (t, id, index, fallback) => t(`plansCompare.${id}.feature.${index}`, fallback);

const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * `currentPlanId` is optional: when omitted (onboarding), every card shows
 * its normal CTA. When provided (dashboard/page), the matching card is
 * marked "Tu plan actual" instead of a CTA, and the Free card's CTA is
 * hidden unless Free is the current plan (no self-serve downgrade path).
 * `renewable` is true when the CURRENT plan is Pro but already scheduled
 * to end (a self-cancelled grace period, or an admin-granted trial with
 * an expiry) — see plans.py's plan_expires_at. Without this, the Pro
 * card would show "Tu plan actual" with no button at all during the
 * grace period, a dead end for the reminder emails that link straight
 * here to let someone renew before losing access.
 * `onClose` is only meaningful when rendered inside a modal (PlansCompareModal);
 * PlansPage doesn't pass one since there's nothing to close.
 */
const PlansCompareCards = ({
  currentPlanId,
  renewable = false,
  title,
  subtitle,
  onClose = () => {},
}) => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const priceLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const resolvedTitle = title || t('plansCompare.title', 'Compara los planes');
  const resolvedSubtitle = subtitle || t('plansCompare.subtitle', 'Elige el plan que mejor se ajuste a tu forma de trabajar.');
  const [billing, setBilling] = useState('monthly');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  // Pro's price is admin-editable (see AdminPlansTab / /admin/plans/pricing)
  // — the hardcoded strings in PLANS above are only the fallback shown
  // until this fetch resolves, so nothing ever flashes empty.
  const [livePricing, setLivePricing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/plans/catalog`).then((res) => {
      if (cancelled) return;
      const pro = res.data?.plans?.find((p) => p.id === 'pro');
      if (pro?.price_clp != null && pro?.price_clp_annual != null) {
        setLivePricing({ price_clp: pro.price_clp, price_clp_annual: pro.price_clp_annual });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const plansToRender = PLANS.map((plan) => {
    if (plan.id !== 'pro' || !livePricing) return plan;
    return {
      ...plan,
      price: `$${livePricing.price_clp.toLocaleString(priceLocale)}`,
      priceYearly: `$${livePricing.price_clp_annual.toLocaleString(priceLocale)}`,
    };
  });

  const handleCta = async (plan) => {
    if (plan.id === 'pro') {
      setSubscribeError('');
      setSubscribing(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/subscriptions/checkout`,
          { billing_period: billing === 'yearly' ? 'annual' : 'monthly' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Fired only after the backend actually created the MercadoPago
        // preapproval and returned a real init_point — a rejected/failed
        // checkout request never counts as a started one. Isolated in its
        // own try/catch: the subscription checkout already exists at this
        // point, so a tracking failure here must never stop the redirect
        // that actually sends the user to pay (this exact failure mode hit
        // the physical-card checkout once already — see ComprarPage.js).
        const amount = billing === 'yearly'
          ? (livePricing?.price_clp_annual ?? 25000)
          : (livePricing?.price_clp ?? 2500);
        try {
          trackInitiateCheckout({ contentId: 'pro_plan', value: amount, currency: 'CLP' });
        } catch (pixelError) {
          console.error('[MetaPixel] InitiateCheckout tracking failed (checkout continues):', pixelError);
        }
        // Same mitigation as ComprarPage.js: fbq() hands off to an async
        // network request, and reassigning location.href in the same tick
        // can tear the page down (cancelling that request) before it ever
        // reaches Meta — confirmed live on /comprar, same redirect pattern
        // here. This short delay gives the beacon a moment to actually
        // leave the browser first.
        setTimeout(() => {
          window.location.href = res.data.init_point;
        }, 250);
      } catch (err) {
        setSubscribeError(
          err?.response?.data?.detail ||
          t('plansCompare.checkoutUnavailable', 'Los pagos en línea aún no están disponibles. Contáctanos para activar tu plan mientras tanto.')
        );
        setSubscribing(false);
      }
      return;
    }
    onClose();
    if (plan.id === 'enterprise') navigate('/agenda-demo');
  };

  return (
    <>
      <div className="prp-head">
        <h2 className="prp-title">{resolvedTitle}</h2>
        <p className="prp-sub">{resolvedSubtitle}</p>
        <div className="prp-billing">
          <button className={`prp-billing-opt ${billing === 'monthly' ? 'prp-billing-opt--on' : ''}`} onClick={() => setBilling('monthly')}>{t('plansCompare.monthly', 'Mensual')}</button>
          <button className={`prp-billing-opt ${billing === 'yearly' ? 'prp-billing-opt--on' : ''}`} onClick={() => setBilling('yearly')}>
            {t('plansCompare.yearly', 'Anual')} <span className="prp-billing-save">{t('plansCompare.twoMonthsFree', '2 meses gratis')}</span>
          </button>
        </div>
      </div>

      <div className="prp-grid">
        {plansToRender.map((plan) => {
          const isCurrent = currentPlanId != null && plan.id === currentPlanId;
          const isRenewablePro = isCurrent && renewable && plan.id === 'pro';
          const showCta = isRenewablePro || (!isCurrent && (plan.id !== 'free' || currentPlanId == null));
          const badgeText = isCurrent
            ? (isRenewablePro ? t('plansCompare.currentPlanEndingSoon', 'Tu plan actual · termina pronto') : t('plansCompare.currentPlan', 'Tu plan actual'))
            : (plan.featured ? t('plansCompare.recommended', 'Recomendado') : null);
          const activePrice = billing === 'yearly' && plan.priceYearly ? plan.priceYearly : plan.price;
          const activeSub = billing === 'yearly' && plan.priceYearlySub ? plan.priceYearlySub : plan.priceSub;
          return (
            <div key={plan.id} className={`prp-plan ${(plan.featured || isCurrent) ? 'prp-plan--featured' : ''}`}>
              {badgeText && <div className="prp-badge">{badgeText}</div>}
              <div className="prp-plan-name">{planT(t, plan.id, 'name', plan.name)}</div>
              <p className="prp-plan-desc">{planT(t, plan.id, 'desc', plan.desc)}</p>
              <div className="prp-price-wrap">
                {plan.contactSales ? (
                  <div className="prp-price-contact">
                    <div className="prp-price-contact-main">{t('plansCompare.contactSales', 'Contacta a ventas')}</div>
                    <p className="prp-price-contact-sub">{planT(t, plan.id, 'priceTagline', plan.priceTagline)}</p>
                  </div>
                ) : (
                  <>
                    {billing === 'yearly' && plan.id === 'pro' && livePricing && (
                      <div className="prp-price-old-row">
                        <span className="prp-price-old">${(livePricing.price_clp * 12).toLocaleString(priceLocale)}</span>
                        <span className="prp-price-note">
                          {t('plansCompare.yearlySavings', `Ahorras $${(livePricing.price_clp * 12 - livePricing.price_clp_annual).toLocaleString(priceLocale)} al año`).replace('{amount}', (livePricing.price_clp * 12 - livePricing.price_clp_annual).toLocaleString(priceLocale))}
                        </span>
                      </div>
                    )}
                    <div className="prp-price-row">
                      <span className="prp-price-main">{activePrice}</span>
                      <span className="prp-price-sub">{planT(t, plan.id, billing === 'yearly' && plan.priceYearlySub ? 'priceYearlySub' : 'priceSub', activeSub)}</span>
                    </div>
                  </>
                )}
              </div>
              <ul className="prp-plan-list">
                {plan.features.map((f, j) => (
                  <li key={j} className="prp-plan-li"><span className="prp-li-check"><IconCheck /></span>{planFeatureT(t, plan.id, j, f)}</li>
                ))}
              </ul>
              {showCta && (
                <button
                  type="button"
                  className={plan.featured ? 'prp-btn-solid' : 'prp-btn-outline'}
                  onClick={() => handleCta(plan)}
                  disabled={plan.id === 'pro' && subscribing}
                >
                  {plan.id === 'pro' && subscribing ? t('plansCompare.redirecting', 'Redirigiendo…') : (isRenewablePro ? t('plansCompare.renewPlan', 'Renovar mi plan') : planT(t, plan.id, 'cta', plan.cta))}
                </button>
              )}
              {plan.id === 'pro' && subscribeError && (
                <p className="prp-subscribe-error">{subscribeError}</p>
              )}
            </div>
          );
        })}
      </div>
      <style>{CSS}</style>
    </>
  );
};

const CSS = `
.prp-head{text-align:center;margin-bottom:28px}
.prp-title{font-size:26px;font-weight:700;color:#000;letter-spacing:-.02em;margin-bottom:8px}
.prp-sub{font-size:14.5px;color:#6e6e73;margin-bottom:18px}
.prp-billing{display:inline-flex;align-items:center;gap:4px;background:rgba(0,0,0,.05);border-radius:10px;padding:4px}
.prp-billing-opt{background:none;border:none;border-radius:8px;padding:8px 18px;font-size:13.5px;font-weight:500;color:#6e6e73;cursor:pointer;font-family:inherit;display:flex;align-items:center;gap:8px}
.prp-billing-opt--on{background:#fff;color:#000;box-shadow:0 1px 4px rgba(0,0,0,.12)}
.prp-billing-save{font-size:10.5px;font-weight:600;background:#000;color:#fff;padding:2px 8px;border-radius:8px}
.prp-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:18px}
.prp-plan{background:#fff;border:1px solid rgba(0,0,0,.1);border-radius:10px;padding:26px 22px;position:relative;display:flex;flex-direction:column}
.prp-plan--featured{border-color:#000;box-shadow:0 8px 28px rgba(0,0,0,.16)}
.prp-badge{display:inline-block;background:rgba(0,0,0,.08);color:#000;border-radius:8px;padding:3px 11px;font-size:10.5px;font-weight:600;margin-bottom:14px;letter-spacing:.01em;width:fit-content}
.prp-plan-name{font-size:19px;font-weight:600;color:#000;letter-spacing:-.01em;margin-bottom:4px}
.prp-plan-desc{font-size:13px;color:#6e6e73;line-height:1.5;margin-bottom:16px;min-height:38px}
.prp-price-wrap{margin:4px 0 16px;padding:14px 0;border-top:1px solid rgba(0,0,0,.06);border-bottom:1px solid rgba(0,0,0,.06)}
.prp-price-old-row{display:flex;align-items:center;gap:6px;margin-bottom:6px}
.prp-price-old{font-size:12px;color:#b0b0b5;text-decoration:line-through}
.prp-price-note{font-size:9.5px;font-weight:600;color:#000;background:rgba(0,0,0,.08);border-radius:8px;padding:2px 8px}
.prp-price-row{display:flex;align-items:baseline;gap:5px}
.prp-price-main{font-size:36px;font-weight:700;line-height:1;letter-spacing:-.03em;color:#000}
.prp-price-sub{font-size:12px;color:#6e6e73}
.prp-price-contact{min-height:44px;display:flex;flex-direction:column;justify-content:center}
.prp-price-contact-main{font-size:18px;font-weight:600;color:#000}
.prp-price-contact-sub{font-size:12px;color:#6e6e73;margin-top:4px;line-height:1.4}
.prp-plan-list{list-style:none;display:flex;flex-direction:column;gap:9px;margin-bottom:20px;flex:1}
.prp-plan-li{font-size:12.5px;color:#1d1d1f;display:flex;align-items:flex-start;gap:8px;line-height:1.4}
.prp-li-check{color:#000;flex-shrink:0;margin-top:1px}
.prp-btn-solid{width:100%;height:44px;border-radius:8px;background:#000;color:#fff;border:none;font-family:inherit;font-size:13.5px;font-weight:600;cursor:pointer;transition:opacity .15s}
.prp-btn-solid:hover{opacity:.82}
.prp-btn-solid:disabled,.prp-btn-outline:disabled{opacity:.6;cursor:not-allowed}
.prp-btn-outline{width:100%;height:44px;border-radius:8px;background:#fff;color:#000;border:1.5px solid rgba(0,0,0,.15);font-family:inherit;font-size:13.5px;font-weight:600;cursor:pointer;transition:background .15s,border-color .15s}
.prp-btn-outline:hover{background:#f7f7f7;border-color:#000}
.prp-subscribe-error{font-size:11.5px;color:#991b1b;margin-top:8px;line-height:1.4;text-align:center}

@media(max-width:760px){
  .prp-grid{grid-template-columns:1fr;gap:14px}
  .prp-plan-desc{min-height:0}
}
`;

export default PlansCompareCards;
